import { create } from 'zustand';
import { SessionStatus, type TestSession, type SessionEvidence } from '@k-ucap/shared';
import { createRepositories } from '@/infrastructure/repositories/RepositoryFactory.js';

export interface CreateSessionParams {
  projectId: string;
  environment: string;
  createdBy: string;
  scenarioId?: string;
  simulationTestId?: string;
  connectionProfileId?: string;
}

export interface SessionState {
  currentSession: TestSession | null;
  isLoading: boolean;
  error: string | null;

  createSession: (params: CreateSessionParams) => Promise<TestSession | null>;
  closeSession: (
    status: SessionStatus,
    counts?: Partial<Pick<TestSession, 'messageCount' | 'passCount' | 'warnCount' | 'failCount'>>
  ) => Promise<void>;
  setSessionContext: (context: Partial<TestSession>) => void;
  addEvidence: (evidence: Omit<SessionEvidence, 'id' | 'createdAt'>) => Promise<void>;
  loadSession: (sessionId: string) => Promise<void>;
  reset: () => void;
}

const { sessionRepository, sessionEvidenceRepository } = createRepositories();

export const useSessionStore = create<SessionState>((set, get) => ({
  currentSession: null,
  isLoading: false,
  error: null,

  createSession: async (params) => {
    set({ isLoading: true, error: null });
    try {
      const now = new Date().toISOString();
      const session: TestSession = {
        id: `session-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        projectId: params.projectId,
        scenarioId: params.scenarioId,
        simulationTestId: params.simulationTestId,
        connectionProfileId: params.connectionProfileId,
        environment: params.environment,
        startedAt: now,
        status: SessionStatus.RUNNING,
        messageCount: 0,
        passCount: 0,
        warnCount: 0,
        failCount: 0,
        createdBy: params.createdBy,
        createdAt: now,
        updatedAt: now,
      };
      await sessionRepository.save(session);
      set({ currentSession: session, isLoading: false });
      return session;
    } catch (err) {
      set({ error: (err as Error).message, isLoading: false });
      return null;
    }
  },

  closeSession: async (status, counts) => {
    const { currentSession } = get();
    if (!currentSession) return;
    const updated: TestSession = {
      ...currentSession,
      ...counts,
      status,
      endedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    try {
      await sessionRepository.save(updated);
      set({ currentSession: updated, isLoading: false });
    } catch (err) {
      set({ error: (err as Error).message, isLoading: false });
    }
  },

  setSessionContext: (context) => {
    const { currentSession } = get();
    if (!currentSession) return;
    set({ currentSession: { ...currentSession, ...context, updatedAt: new Date().toISOString() } });
  },

  addEvidence: async (evidence) => {
    try {
      const item = {
        ...evidence,
        id: `evidence-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        createdAt: new Date().toISOString(),
      } as SessionEvidence;
      await sessionEvidenceRepository.save(item);
    } catch (err) {
      // Evidence 저장 실패는 세션 흐름을 중단하지 않음
      console.error('Failed to save session evidence:', err);
    }
  },

  loadSession: async (sessionId) => {
    set({ isLoading: true, error: null });
    try {
      const session = await sessionRepository.findById(sessionId);
      if (session) {
        set({ currentSession: session, isLoading: false });
      } else {
        set({ error: 'Session not found', isLoading: false });
      }
    } catch (err) {
      set({ error: (err as Error).message, isLoading: false });
    }
  },

  reset: () => set({ currentSession: null, isLoading: false, error: null }),
}));

export default useSessionStore;
